import { ForwardedRef, PropsWithChildren, ReactElement } from 'react';
import { FormFieldCheckbox } from '@client/common/components/Form/FormFieldCheckbox';
import { FormFieldSelectable } from '@client/common/components/Form/FormFieldSelectable';
import { FORM_FIELD_TEXT_TYPE, FormFieldText } from '@client/common/components/Form/FormFieldText';
import { FormFieldTextArea } from '@client/common/components/Form/FormFieldTextArea';
import { useTranslate } from '@client/i18n/hooks';
import { PDFContent } from '@client/product/components/ProductActions/PDFAction/PDFContent';
import { ProductFullType } from '@client/product/redux/products/full/types';
import { useFormikContext } from 'formik';
import { ModalButtons } from 'b2b-components/core/components/Modal';
import { Radio } from 'b2b-components/theme/components/Form/Radio';
import { FIELD_NAME, FormValuesType } from '../PDFFormContainer.types';
import { Props } from './index';
import './Body.scss';

export const Body = ({
  generatePDF,
  PDFContentRef,
  printPDF,
  product,
  toggleModal,
  isPrintInProgress,
  isDownloadInProgress,
  text,
}: Props): ReactElement => {
  const { values } = useFormikContext<FormValuesType>();
  const t = useTranslate();
  const { effectivePricing } = product;
  const PDFContentRefComponent = React.forwardRef((
    props: PropsWithChildren<{product: ProductFullType, values: FormValuesType }>,
    ref: ForwardedRef<HTMLDivElement | null>,
  ) => {
    const {
      product,
      values,
    } = props;
    return (
      <div ref={ref}>
        <PDFContent product={product} values={values} />
      </div>
    );
  });
  PDFContentRefComponent.displayName = 'PDFContentRefComponent';

  const renderCustomPricesRadioButtons = ({
    isOptionChecked,
    onOptionChange,
  }: {
    isOptionChecked: (field: string) => boolean,
    onOptionChange: (field: string) => void,
  }): ReactElement => (
    <>
      <Radio
        isChecked={isOptionChecked(FIELD_NAME.withVAT)}
        key={FIELD_NAME.withVAT}
        label={t('with VAT')}
        onChange={() => onOptionChange(FIELD_NAME.withVAT)}
      />

      <Radio
        isChecked={isOptionChecked(FIELD_NAME.withoutVAT)}
        key={FIELD_NAME.withoutVAT}
        label={t('without VAT')}
        onChange={() => onOptionChange(FIELD_NAME.withoutVAT)}
      />
    </>
  );

  const renderFixedPricesRadioButtons = ({
    isOptionChecked,
    onOptionChange,
  }: {
    isOptionChecked: (fieldName: string) => boolean,
    onOptionChange: (fieldName: string) => void
  }) => (
    <>
      <Radio
        isChecked={isOptionChecked(FIELD_NAME.listPrice)}
        isDisabled={effectivePricing.isHiddenPriceAndNotCertified}
        key={FIELD_NAME.listPrice}
        label={
          <span>
            {text?.listPrice || t(
              'SRP incl. {taxRate} VAT',
              {
                taxRate: `${effectivePricing.priceTaxRate}%`,
              },
              'Suggested Retail Price with VAT',
            )} (CHF{' '}
            <strong>{effectivePricing.listPrice.toFixed(2)}</strong>)
          </span>
        }
        onChange={() => onOptionChange(FIELD_NAME.listPrice)}
      />

      <Radio
        isChecked={isOptionChecked(FIELD_NAME.listPriceWithoutVat)}
        isDisabled={effectivePricing.isHiddenPriceAndNotCertified}
        key={FIELD_NAME.listPriceWithoutVat}
        label={
          <span>
            {text?.listPriceWithoutVat || t(
              'SRP excl. {taxRate} VAT',
              {
                taxRate: `${effectivePricing.priceTaxRate}%`,
              },
              'Suggested Retail Price Without VAT',
            )} (CHF{' '}
            <strong>{effectivePricing.listPriceWithoutVat.toFixed(2)}</strong>)
          </span>
        }
        onChange={() => onOptionChange(FIELD_NAME.listPriceWithoutVat)}
      />

      {effectivePricing.hasB2BUserPrices && (
        <>
          <Radio
            isChecked={isOptionChecked(FIELD_NAME.userPrice)}
            key={FIELD_NAME.userPrice}
            label={
              <span>
                {t(
                  'PP incl. {taxRate} VAT',
                  {
                    taxRate: `${effectivePricing.priceTaxRate}%`,
                  },
                  'Purchase Price with VAT',
                )} (CHF{' '}
                <strong>{effectivePricing.userPrice.toFixed(2)}</strong>)
              </span>
            }
            onChange={() => onOptionChange(FIELD_NAME.userPrice)}
          />

          <Radio
            isChecked={isOptionChecked(FIELD_NAME.userPriceWithoutVat)}
            key={FIELD_NAME.userPriceWithoutVat}
            label={
              <span>
                {t(
                  'PP excl. {taxRate} VAT',
                  {
                    taxRate: `${effectivePricing.priceTaxRate}%`,
                  },
                  'Purchase Price without VAT',
                )}
                (CHF{' '}
                <strong>
                  {effectivePricing.userPriceWithoutVat.toFixed(2)}
                </strong>
                )
              </span>
            }
            onChange={() => onOptionChange(FIELD_NAME.userPriceWithoutVat)}
          />
        </>
      )}
    </>
  );

  return (
    <form className='PDFForm'>
      <p className='PDFForm-Subtitle'>
        {t('Create a custom PDF file using variables available below:')}
      </p>

      <div className='PDFForm-Content'>
        <div className='PDFForm-Section'>
          <h3 className='PDFForm-SectionHeader'>{t('Product details:')}</h3>

          <FormFieldCheckbox
            key={FIELD_NAME.productName}
            name={FIELD_NAME.productName}
            innerComponentProps={{
              isDefaultChecked: values[FIELD_NAME.productName],
              isDisabled: true,
              label: t('Product name'),
            }}
          />

          <FormFieldCheckbox
            key={FIELD_NAME.manufacturerNumber}
            name={FIELD_NAME.manufacturerNumber}
            innerComponentProps={{
              isDefaultChecked: values[FIELD_NAME.manufacturerNumber],
              isDisabled: true,
              label: t('Manufacturer number'),
            }}
          />

          <FormFieldCheckbox
            key={FIELD_NAME.shortInfo}
            name={FIELD_NAME.shortInfo}
            innerComponentProps={{
              isDefaultChecked: values[FIELD_NAME.shortInfo],
              isDisabled: true,
              label: t('Short product information'),
            }}
          />

          <FormFieldCheckbox
            key={FIELD_NAME.mainImage}
            name={FIELD_NAME.mainImage}
            innerComponentProps={{
              isDefaultChecked: values[FIELD_NAME.mainImage],
              label: t('Main image'),
            }}
          />

          <FormFieldCheckbox
            key={FIELD_NAME.additionalImages}
            name={FIELD_NAME.additionalImages}
            innerComponentProps={{
              isDefaultChecked: values[FIELD_NAME.additionalImages],
              label: t('Additional product images'),
            }}
          />

          <FormFieldCheckbox
            key={FIELD_NAME.sku}
            name={FIELD_NAME.sku}
            innerComponentProps={{
              isDefaultChecked: values[FIELD_NAME.sku],
              label: t('SKU'),
            }}
          />

          <FormFieldCheckbox
            key={FIELD_NAME.warrantyInfo}
            name={FIELD_NAME.warrantyInfo}
            innerComponentProps={{
              isDefaultChecked: values[FIELD_NAME.warrantyInfo],
              label: t('Warranty information'),
            }}
          />

          <FormFieldCheckbox
            key={FIELD_NAME.description}
            name={FIELD_NAME.description}
            innerComponentProps={{
              isDefaultChecked: values[FIELD_NAME.description],
              label: t('Description'),
            }}
          />

          <FormFieldCheckbox
            key={FIELD_NAME.specification}
            name={FIELD_NAME.specification}
            innerComponentProps={{
              isDefaultChecked: values[FIELD_NAME.specification],
              label: t('Technical specifications'),
            }}
          />
        </div>

        <div className="PDFForm-Section">
          <h6 className="PDFForm-SectionHeader">{t('Pricing')}</h6>

          <FormFieldCheckbox
            key={FIELD_NAME.offer}
            name={FIELD_NAME.offer}
            innerComponentProps={{
              isDefaultChecked: values[FIELD_NAME.offer],
              label: t('Offered price'),
            }}
          />

          <div className="PDFForm-Indent PDFForm-CustomPriceBox">
            <span className="PDFForm-CustomPriceBox--Currency">{'CHF'}</span>

            <FormFieldText
              name={FIELD_NAME.offerPriceValue}
              innerComponentProps={{
                className: 'PDFForm-CustomPriceBox--Price',
                type: FORM_FIELD_TEXT_TYPE.number,
              }}
            />

            <FormFieldSelectable
              name={FIELD_NAME.offerValue}
              innerComponentProps={{
                className: 'PDFForm-CustomPriceBox--RadioButtons',
                isMultiple: false,
                render: renderCustomPricesRadioButtons,
              }}
            />
          </div>

          <FormFieldCheckbox
            key={FIELD_NAME.fixedPrices}
            name={FIELD_NAME.fixedPrices}
            innerComponentProps={{
              isDefaultChecked: values[FIELD_NAME.fixedPrices],
              isDisabled: effectivePricing.isHiddenPriceAndNotCertified,
              label: t('Fixed prices'),
            }}
          />

          <FormFieldSelectable
            name={FIELD_NAME.fixedPricesTaxValue}
            innerComponentProps={{
              className: 'PDFForm-Indent',
              isMultiple: false,
              render: renderFixedPricesRadioButtons,
            }}
          />
        </div>
      </div>

      <FormFieldTextArea
        key={FIELD_NAME.additionalComments}
        name={FIELD_NAME.additionalComments}
        innerComponentProps={{
          maxLength: 200,
          info: t(['Maximum 1 character', 'Maximum {n} characters', 'n'], {
            n: 200,
          }),
          text: {
            label: t('Additional comments in PDF'),
          },
        }}
      />

      <ModalButtons
        primary={{
          label: t('Export PDF file'),
          isLoading: isDownloadInProgress,
          onClick: generatePDF,
        }}
        secondary={{
          label: t('Print'),
          isLoading: isPrintInProgress,
          onClick: printPDF,
        }}
        cancel={{
          label: t('Cancel'),
          onClick: toggleModal,
        }}
      />

      <PDFContentRefComponent
        product={product}
        ref={PDFContentRef}
        values={values}
      />
    </form>
  );
};
